import { useMemo } from "react";
import { IconAward, IconLock } from "@tabler/icons-react";
import { Trans } from "@lingui/macro";

import { achievements as allAchievements } from "../../content";
import { useLocalize } from "../../content/useLocalize";
import type { AchievementId } from "../../types";
import { cx } from "../common/cx";
import styles from "./AchievementsPanel.module.css";

export interface AchievementsPanelProps {
  earnedIds: readonly AchievementId[];
  highlightIds?: readonly AchievementId[];
  compact?: boolean;
}

export function AchievementsPanel({
  earnedIds,
  highlightIds = [],
  compact = false,
}: AchievementsPanelProps): JSX.Element {
  const t = useLocalize();
  const earned = useMemo(() => new Set(earnedIds), [earnedIds]);
  const highlighted = useMemo(() => new Set(highlightIds), [highlightIds]);

  const visible = compact
    ? allAchievements.filter((achievement) => earned.has(achievement.id))
    : allAchievements;
  const earnedCount = earned.size;
  const totalCount = allAchievements.length;
  const lockedCount = totalCount - earnedCount;

  return (
    <ul className={styles.list}>
      {compact && (
        <li className={styles.summary}>
          <Trans>
            Получено {earnedCount} из {totalCount}
          </Trans>
          {lockedCount > 0 && (
            <span className={styles.summaryHint}>
              <Trans>ещё {lockedCount} впереди</Trans>
            </span>
          )}
        </li>
      )}
      {visible.map((achievement) => {
        const isEarned = earned.has(achievement.id);
        const isNew = highlighted.has(achievement.id);
        return (
          <li
            key={achievement.id}
            className={cx(styles.item, !isEarned && styles.locked, isNew && styles.fresh)}
          >
            <span className={styles.icon} aria-hidden="true">
              {isEarned ? <IconAward size={17} /> : <IconLock size={17} />}
            </span>
            <span className={styles.body}>
              <span className={styles.name}>{t(achievement.name)}</span>
              <span className={styles.description}>{t(achievement.description)}</span>
            </span>
            <span className={styles.state}>
              {isEarned ? <Trans>получено</Trans> : <Trans>не получено</Trans>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
